import React from 'react';
import { format, parseISO } from 'date-fns';
import {
  Check,
  CheckCheck,
  Edit3,
  MoreVertical,
  Reply,
  Image as ImageIcon,
  FileText,
  Download,
  Bot,
  Loader2,
  X,
  Volume2,
  Play,
  Pause
} from 'lucide-react';
import { Message, User } from '../../types';
import { BookingCard } from './BookingCard';

// Utility function to convert URLs to clickable links
const linkifyText = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

// Utility function to parse UTC timestamp correctly
// Handles timestamps that don't have explicit timezone markers
const parseUTCTimestamp = (utcString: string): Date => {
  try {
    // If it already has 'Z' at the end, it's explicit UTC
    if (utcString.endsWith('Z')) {
      return parseISO(utcString);
    }
    
    // Check if it has a timezone offset (e.g., +05:30, -05:00)
    const hasTimezoneOffset = /[+-]\d{2}:\d{2}$/.test(utcString);
    if (hasTimezoneOffset) {
      return parseISO(utcString);
    }
    
    // If it's an ISO format timestamp without timezone info (e.g., "2025-10-28T12:05:46.713948"),
    // assume it's UTC and append 'Z' to make it explicit.
    // This ensures JavaScript will properly convert it from UTC to local timezone (IST +5:30)
    if (utcString.includes('T')) {
      return parseISO(utcString + 'Z');
    }
    
    // Fallback: try to parse as is
    return parseISO(utcString);
  } catch (error) {
    console.error('Error parsing UTC timestamp:', utcString, error);
    // Fallback to regular Date constructor
    return new Date(utcString);
  }
};

interface MessageBubbleProps {
  message: Message;
  currentUser: User;
  isConsecutive?: boolean;
  onReply?: (message: Message) => void;
  onEdit?: (message: Message) => void;
  onDelete?: (message: Message) => void;
  onRetry?: (message: Message) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = React.memo(({
  message,
  currentUser,
  isConsecutive = false,
  onReply,
  onEdit,
  onDelete,
  onRetry,
}) => {
  const isOwnMessage = message.byBoz;
  const isBotMessage = message.createdByName === 'BOT';
  const isNoteMessage = message.type === 'Note';
  const isBookingMessage = message.type === 'Booking';

  const getMessageContent = () => {
    if (message.receivedData?.message?.message) {
      return message.receivedData.message.message;
    }
    if (message.sendMessage?.data?.message) {
      return message.sendMessage.data.message;
    }
    if (message.any?.note) {
      return `Note: ${message.any.note}`;
    }
    return '';
  };

  const getMediaUrl = () => {
    // For BOT messages, prioritize mediaUrl field
    if (message.createdByName === 'BOT' && message.sendMessage?.data?.mediaUrl) {
      console.log('Found BOT sendMessage mediaUrl:', message.sendMessage.data.mediaUrl);
      return message.sendMessage.data.mediaUrl;
    }
    if (message.createdByName === 'BOT' && message.receivedData?.message?.mediaUrl) {
      console.log('Found BOT receivedData mediaUrl:', message.receivedData.message.mediaUrl);
      return message.receivedData.message.mediaUrl;
    }

    // For sent image messages (including byBoz messages), check sendMessage data first
    if (message.sendMessage?.data?.mediaUrl) {
      console.log('Found sendMessage mediaUrl:', message.sendMessage.data.mediaUrl);
      return message.sendMessage.data.mediaUrl;
    }
    
    // For sent image messages, the URL is in the message content
    if (message.type === 'Image' && message.sendMessage?.data?.message) {
      console.log('Found Image type sendMessage message:', message.sendMessage.data.message);
      return message.sendMessage.data.message;
    }
    
    // Check for media_url field (new format)
    if (message.receivedData?.message?.media_url) {
      console.log('Found receivedData media_url:', message.receivedData.message.media_url);
      return message.receivedData.message.media_url;
    }
    
    // Check for mediaUrl field (legacy format)
    if (message.receivedData?.message?.mediaUrl) {
      console.log('Found receivedData mediaUrl:', message.receivedData.message.mediaUrl);
      return message.receivedData.message.mediaUrl;
    }
    
    // For BOT messages, check if the message content itself is a URL (fallback)
    if (message.createdByName === 'BOT' && message.receivedData?.message?.message) {
      const messageContent = message.receivedData.message.message;
      // Check if the message content is a valid URL (starts with http/https)
      if (messageContent && (messageContent.startsWith('http://') || messageContent.startsWith('https://'))) {
        console.log('Found BOT message content URL:', messageContent);
        return messageContent;
      }
    }
    // For BOT messages with sendMessage data
    if (message.createdByName === 'BOT' && message.sendMessage?.data?.message) {
      const messageContent = message.sendMessage.data.message;
      // Check if the message content is a valid URL (starts with http/https)
      if (messageContent && (messageContent.startsWith('http://') || messageContent.startsWith('https://'))) {
        console.log('Found BOT sendMessage content URL:', messageContent);
        return messageContent;
      }
    }
    
    console.log('No media URL found for message:', message);
    return null;
  };

  const isDocumentMessage = () => {
    // Check if it's explicitly marked as Document type
    if (message.type === 'Document') {
      return true;
    }
    
    // Check sendMessage data for Document type
    if (message.sendMessage?.type === 'Document' || message.sendMessage?.data?.type === 'Document') {
      return true;
    }
    
    return false;
  };

  const isImageMessage = () => {
    // Don't treat documents as images
    if (isDocumentMessage()) {
      return false;
    }
    
    // Check if it's explicitly marked as Image type
    if (message.type === 'Image' || message.receivedData?.message?.message_content_type === 'Image') {
      return true;
    }
    
    // For BOT messages, check if they have a mediaUrl (most common case)
    if (message.createdByName === 'BOT') {
      if (message.sendMessage?.data?.mediaUrl || message.receivedData?.message?.mediaUrl || message.receivedData?.message?.media_url) {
        // But exclude if it's a PDF/document
        const url = message.sendMessage?.data?.mediaUrl || message.receivedData?.message?.mediaUrl || message.receivedData?.message?.media_url;
        if (url && (url.includes('.pdf') || url.toLowerCase().endsWith('.pdf'))) {
          return false;
        }
        return true;
      }
      
      // Fallback: check if the message content is a URL (likely an image)
      const messageContent = message.receivedData?.message?.message || message.sendMessage?.data?.message;
      if (messageContent && (messageContent.startsWith('http://') || messageContent.startsWith('https://'))) {
        // But exclude if it's a PDF/document
        if (messageContent.includes('.pdf') || messageContent.toLowerCase().endsWith('.pdf')) {
          return false;
        }
        return true;
      }
    }
    
    // Check if we have a media URL and it's not an audio message or document
    const mediaUrl = getMediaUrl();
    const contentType = message.receivedData?.message?.message_content_type;
    if (mediaUrl && (mediaUrl.includes('.pdf') || mediaUrl.toLowerCase().endsWith('.pdf'))) {
      return false;
    }
    return !!(mediaUrl && contentType !== 'Audio');
  };

  const isAudioMessage = () => {
    // Check if it's explicitly marked as Audio type
    if (message.type === 'Audio' || message.receivedData?.message?.message_content_type === 'Audio') {
      console.log('Detected audio message by type:', message.type, message.receivedData?.message?.message_content_type);
      return true;
    }
    
    // Check if we have a media URL and it's an audio message
    const mediaUrl = getMediaUrl();
    const contentType = message.receivedData?.message?.message_content_type;
    const isAudio = !!(mediaUrl && contentType === 'Audio');
    console.log('Audio message check:', { mediaUrl, contentType, isAudio });
    return isAudio;
  };

  const getDocumentUrl = () => {
    // Check sendMessage data first (for sent documents)
    if (message.sendMessage?.data?.mediaUrl) {
      return message.sendMessage.data.mediaUrl;
    }
    
    // Check receivedData for received documents
    if (message.receivedData?.message?.media_url) {
      return message.receivedData.message.media_url;
    }
    
    if (message.receivedData?.message?.mediaUrl) {
      return message.receivedData.message.mediaUrl;
    }
    
    // Check if message content itself is a PDF URL
    const content = getMessageContent();
    if (content && (content.endsWith('.pdf') || content.includes('.pdf'))) {
      return content;
    }
    
    return null;
  };

  const getDocumentName = () => {
    const url = getDocumentUrl();
    if (!url) return 'Document';
    
    // Extract filename from URL
    try {
      const urlParts = url.split('/');
      const filename = urlParts[urlParts.length - 1];
      return filename || 'Document';
    } catch {
      return 'Document';
    }
  };

  const getReadStatusIcon = () => {
    if (!isOwnMessage) return null;
    
    if (message.status === 'sending') {
      return <Loader2 className="h-3 w-3 text-gray-400 animate-spin" />;
    }
    
    if (message.status === 'failed') {
      return <X className="h-3 w-3 text-red-500" />;
    }
    
    // For sent messages, show double check
    return <CheckCheck className="h-3 w-3 text-blue-500" />;
  };

  const renderAudioPlayer = (audioUrl: string) => {
    return (
      <div className="mt-2">
        <div className="flex items-center space-x-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg max-w-xs">
          <div className="flex-shrink-0">
            <Volume2 className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <audio 
              controls 
              className="w-full h-8"
              preload="metadata"
              onError={(e) => {
                console.error('Audio load error:', e);
                const target = e.target as HTMLAudioElement;
                console.error('Audio URL:', target.src);
                console.error('Audio error details:', target.error);
              }}
              onLoadStart={() => console.log('Audio loading started:', audioUrl)}
              onCanPlay={() => console.log('Audio can play:', audioUrl)}
              onPlay={() => console.log('Audio started playing:', audioUrl)}
              onPause={() => console.log('Audio paused:', audioUrl)}
              onEnded={() => console.log('Audio ended:', audioUrl)}
            >
              <source src={audioUrl} type="audio/ogg" />
              <source src={audioUrl} type="audio/mpeg" />
              <source src={audioUrl} type="audio/wav" />
              <source src={audioUrl} type="audio/webm" />
              <source src={audioUrl} type="audio/mp4" />
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>
      </div>
    );
  };

  const renderAttachment = (attachment: any) => {
    if (attachment.type.startsWith('image/')) {
      return (
        <div className="mt-2">
          <img
            src={attachment.url}
            alt={attachment.name}
            className="max-w-xs rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => window.open(attachment.url, '_blank')}
          />
        </div>
      );
    }
    
    return (
      <div className="mt-2 flex items-center space-x-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {attachment.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {(attachment.size / 1024).toFixed(1)} KB
          </p>
        </div>
        <button
          onClick={() => window.open(attachment.url, '_blank')}
          className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          aria-label={`Download ${attachment.name}`}
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
    );
  };

  const renderDocument = (documentUrl: string) => {
    const documentName = getDocumentName();
    const isPdf = documentUrl.toLowerCase().endsWith('.pdf') || documentUrl.includes('.pdf');
    
    return (
      <div className={`${getMessageContent() && getMessageContent() !== "None" && getMessageContent() !== "File" ? "mb-2" : ""}`}>
        <div className={`flex items-center space-x-3 p-3 rounded-lg max-w-xs cursor-pointer hover:bg-opacity-90 transition-all ${
          isOwnMessage 
            ? 'bg-white bg-opacity-20' 
            : 'bg-gray-100'
        }`}
        onClick={() => window.open(documentUrl, '_blank')}
        >
          <div className="flex-shrink-0">
            <FileText className={`h-6 w-6 ${isOwnMessage ? 'text-white' : 'text-red-500'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium truncate ${
              isOwnMessage ? 'text-white' : 'text-gray-900'
            }`}>
              {documentName}
            </p>
            <p className={`text-xs ${
              isOwnMessage ? 'text-white text-opacity-80' : 'text-gray-500'
            }`}>
              PDF Document
            </p>
          </div>
          <div className="flex-shrink-0">
            <Download className={`h-4 w-4 ${
              isOwnMessage ? 'text-white' : 'text-gray-500'
            }`} />
          </div>
        </div>
      </div>
    );
  };

  if (isBookingMessage) {
    return (
      <div className="flex justify-center my-4">
        <div className="max-w-md w-full">
          <BookingCard booking={message.any} />
        </div>
      </div>
    );
  }

  if (isNoteMessage) {
    return (
      <div className="flex justify-center my-2">
        <div className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1 rounded-lg flex items-center space-x-1 max-w-md">
          <span className="font-medium">{message.any?.title}:</span>
          <span>{message.any?.note}</span>
        </div>
      </div>
    );
  }

  if (isBotMessage) {
    // For BOT messages, render them like regular messages but right-aligned
    return (
      <div className="flex justify-end mb-1">
        <div className="flex max-w-xs lg:max-w-md flex-row-reverse">
          {/* Avatar */}
          <div className="flex-shrink-0 ml-2">
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              <Bot className="h-4 w-4 text-blue-600" />
            </div>
          </div>

          {/* Message Content */}
          <div className="flex flex-col items-end">
            {/* Sender Name */}
            <span className="text-xs text-gray-500 dark:text-gray-400 mb-1 px-1">
              BOT
            </span>

            {/* Message Bubble */}
            <div className="group relative">
              <div className="px-4 py-2 rounded-2xl bg-blue-100 text-blue-800 mt-1">
                {/* Image Message */}
                {isImageMessage() && (
                  <div className={getMessageContent() ? "mb-2" : ""}>
                    <div className="relative group">
                      <img
                        src={getMediaUrl()!}
                        alt={getMessageContent() || 'Image'}
                        className="max-w-xs max-h-64 rounded-lg cursor-pointer hover:opacity-90 transition-opacity object-cover"
                        onClick={() => window.open(getMediaUrl()!, '_blank')}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          // Show error message instead
                          const errorDiv = document.createElement('div');
                          errorDiv.className = 'max-w-xs max-h-64 rounded-lg bg-gray-100 flex items-center justify-center p-4 text-gray-500 text-sm';
                          errorDiv.textContent = 'Failed to load image';
                          target.parentNode?.insertBefore(errorDiv, target);
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-lg flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Audio Message */}
                {isAudioMessage() && (
                  <div className={getMessageContent() && getMessageContent() !== "None" ? "mb-2" : ""}>
                    {renderAudioPlayer(getMediaUrl()!)}
                  </div>
                )}
                
                {/* Message Content */}
                {getMessageContent() && getMessageContent() !== "None" && (
                  <div className="whitespace-pre-wrap break-words">
                    {linkifyText(getMessageContent())}
                  </div>
                )}
              </div>
            </div>

            {/* Timestamp */}
            <div className="flex items-center space-x-1 mt-1 px-1 flex-row-reverse space-x-reverse">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {(() => {
                  // For customer messages (byBoz: false), use received_at_utc
                  if (!message.byBoz && message.receivedData?.message?.received_at_utc) {
                    const utcDate = parseUTCTimestamp(message.receivedData.message.received_at_utc);
                    return format(utcDate, 'h:mm a');
                  }
                  // Fallback to timestamp or createdOn
                  if (message.timestamp) {
                    return format(new Date(message.timestamp), 'h:mm a');
                  }
                  if (message.createdOn) {
                    // createdOn is in milliseconds
                    return format(new Date(message.createdOn), 'h:mm a');
                  }
                  return '';
                })()}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-1`}>
      <div className={`flex max-w-xs lg:max-w-md ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar (only show if not consecutive) */}
        {!isConsecutive && (
          <div className={`flex-shrink-0 ${isOwnMessage ? 'ml-2' : 'mr-2'}`}>
            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-xs font-medium text-primary-600">
                {message.createdByName.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        )}

        {/* Message Content */}
        <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
          {/* Sender Name (only show if not consecutive) */}
          {!isConsecutive && (
            <span className="text-xs text-gray-500 dark:text-gray-400 mb-1 px-1">
              {message.createdByName}
            </span>
          )}

              {/* Message Bubble */}
              <div className="group relative">
                <div
                  className={`px-4 py-2 rounded-2xl ${
                    isOwnMessage
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                  } ${isConsecutive ? 'mt-0' : 'mt-1'}`}
                >
                  {/* Image Message */}
                  {isImageMessage() && (
                    <div className={getMessageContent() ? "mb-2" : ""}>
                      <div className="relative group">
                        <img
                          src={getMediaUrl()!}
                          alt={getMessageContent() || 'Image'}
                          className="max-w-xs max-h-64 rounded-lg cursor-pointer hover:opacity-90 transition-opacity object-cover"
                          onClick={() => window.open(getMediaUrl()!, '_blank')}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            // Show error message instead
                            const errorDiv = document.createElement('div');
                            errorDiv.className = 'max-w-xs max-h-64 rounded-lg bg-gray-100 flex items-center justify-center p-4 text-gray-500 text-sm';
                            errorDiv.textContent = 'Failed to load image';
                            target.parentNode?.insertBefore(errorDiv, target);
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-lg flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Audio Message */}
                  {isAudioMessage() && (
                    <div className={getMessageContent() && getMessageContent() !== "None" ? "mb-2" : ""}>
                      {renderAudioPlayer(getMediaUrl()!)}
                    </div>
                  )}

                  {/* Document Message */}
                  {isDocumentMessage() && getDocumentUrl() && (
                    renderDocument(getDocumentUrl())
                  )}
                  
                  {/* Message Content */}
                  {getMessageContent() && getMessageContent() !== "None" && getMessageContent() !== "File" && (
                    <div className="whitespace-pre-wrap break-words">
                      {linkifyText(getMessageContent())}
                    </div>
                  )}
                </div>

            {/* Message Actions */}
            <div className={`absolute top-0 ${
              isOwnMessage ? 'left-0 transform -translate-x-full' : 'right-0 transform translate-x-full'
            } opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-1`}>
              <button
                onClick={() => onReply?.(message)}
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
                aria-label="Reply to message"
              >
                <Reply className="h-3 w-3" />
              </button>
              
              {isOwnMessage && (
                <>
                  <button
                    onClick={() => onEdit?.(message)}
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
                    aria-label="Edit message"
                  >
                    <Edit3 className="h-3 w-3" />
                  </button>
                  
                  <button
                    onClick={() => onDelete?.(message)}
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 rounded"
                    aria-label="Delete message"
                  >
                    <MoreVertical className="h-3 w-3" />
                  </button>
                </>
              )}
            </div>
          </div>

              {/* Timestamp and Read Status */}
              <div className={`flex items-center space-x-1 mt-1 px-1 ${
                isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''
              }`}>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {(() => {
                    // For customer messages (byBoz: false), use received_at_utc
                    if (!message.byBoz && message.receivedData?.message?.received_at_utc) {
                      const utcDate = parseUTCTimestamp(message.receivedData.message.received_at_utc);
                      return format(utcDate, 'h:mm a');
                    }
                    // Fallback to timestamp or createdOn
                    if (message.timestamp) {
                      return format(new Date(message.timestamp), 'h:mm a');
                    }
                    if (message.createdOn) {
                      // createdOn is in milliseconds
                      return format(new Date(message.createdOn), 'h:mm a');
                    }
                    return '';
                  })()}
                </span>
                {getReadStatusIcon()}
                {message.status === 'failed' && onRetry && (
                  <button
                    onClick={() => onRetry(message)}
                    className="ml-1 text-xs text-red-600 hover:text-red-800 underline"
                    aria-label="Retry sending message"
                  >
                    Retry
                  </button>
                )}
              </div>
        </div>
      </div>
    </div>
  );
});
